import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useGetPublicList from '../hooks/useGetPublicList';
import { Button } from '@/components/ui/button';
import WishCard from '@/features/Wish/components/WishCard';
import useGetPublicWishes from '@/features/Wish/hooks/useGetPublicWishes';
import { useTranslation } from 'react-i18next';
import {
	ArrowLeft,
	Cake,
	LogIn,
	MoreVertical,
	Save,
	Send,
	Trash2,
	User,
} from 'lucide-react';
import useGetUser from '@/features/User/hooks/useGetUser';
import WishSharedDetail from '@/features/Wish/components/WishSharedDetail';
import useGetSavedLists from '../hooks/useGetSavedLists';
import useSaveList from '../hooks/useSaveList';
import { ListSaved } from '../list';
import { formatDate, getFutureAge } from '@/utils/date';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useEffect, useState } from 'react';
import ListSavedDeleteModal from '../components/ListSavedDeleteModal';
import ListShareModal from '../components/ListShareModal';
import Navbar from '@/components/Navbar';

const ListShared = () => {
	const { t } = useTranslation();
	const { public_uid } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const { list } = useGetPublicList(public_uid || '');
	const { wishes } = useGetPublicWishes(public_uid || '');
	const { user } = useGetUser();
	const { listsSaved } = useGetSavedLists();

	const { saveList } = useSaveList(list as ListSaved);

	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	if (!public_uid || !list) return null;

	const alreadySaved = listsSaved.some(
		(listSaved) => listSaved.public_uid === public_uid
	);

	const isMobile = window.innerWidth < 640;

	const renderMainAction = () => {
		if (user) {
			return (
				<Button
					disabled={user.id === list.fk_user || alreadySaved}
					className="w-full sm:w-fit"
					onClick={() => {
						saveList({ public_uid: list.public_uid });
					}}
				>
					<Save className="mr-2 h-4 w-4" />
					{t('sharedList.save.action')}
				</Button>
			);
		}
		return (
			<Button
				className="w-full sm:w-fit"
				onClick={() => navigate('/?redirect=' + location.pathname)}
			>
				<LogIn className="mr-2 h-4 w-4" />
				{t('sharedList.save.login')}
			</Button>
		);
	};

	const renderDropdown = () => {
		return (
			<DropdownMenu modal={false}>
				<DropdownMenuTrigger asChild>
					<MoreVertical className="cursor-pointer" size={20} />
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					<DropdownMenuItem onClick={() => setOpenDeleteModal(true)}>
						<Trash2 className="mr-2 h-4 w-4" />
						<span>Supprimer de vos listes</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	const renderList = () => {
		return (
			<div className="w-full p-0">
				<div className="flex justify-between">
					{alreadySaved || list.fk_user === user?.id ? (
						<>
							<div className="flex items-center gap-2">
								<ArrowLeft
									className="mr-2 cursor-pointer"
									onClick={() => navigate('/lists')}
								/>
							</div>
							<ListShareModal
								list={list}
								trigger={
									<Button
										variant="outline"
										className="flex items-center"
									>
										<Send className="mr-2 h-4 w-4" />
										{t('common.share')}
									</Button>
								}
							/>
						</>
					) : (
						<div className="hidden w-full sm:flex sm:justify-end">
							{renderMainAction()}
						</div>
					)}
				</div>
				<div className="mt-4 flex items-center">
					<span className="text-2xl font-bold">{list.name}</span>
					{alreadySaved && (
						<span className="inline-flex items-center gap-1 whitespace-nowrap">
							&#xfeff;
							<span className="inline-block">
								{renderDropdown()}
							</span>
						</span>
					)}
				</div>
				<div className="mt-2 flex flex-wrap gap-1">
					<div className="flex rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
						<User className="mr-2 h-4 w-4" />
						{list.user.firstname} {list.user.lastname}
					</div>
					<div className="flex rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground ">
						<Cake className="mr-2 h-4 w-4" />
						{formatDate(list.user.birthday as string)}
					</div>
					<div className="flex rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground ">
						{getFutureAge(list.user.birthday as string)}
						{t('listSaved.age.suffix')}
					</div>
				</div>
				<div className="mt-4 flex flex-col gap-3 pb-24">
					{wishes?.map((wish) => (
						<WishCard
							key={wish.id}
							onClick={() =>
								navigate(`/shared/${public_uid}/${wish.id}`)
							}
							wish={wish}
							list={list}
						/>
					))}
				</div>

				<WishSharedDetail />

				{!alreadySaved && (
					<div className="fixed bottom-0 left-0 w-full border-t border-secondary bg-background px-4 py-4 sm:hidden">
						{renderMainAction()}
					</div>
				)}

				{openDeleteModal && (
					<ListSavedDeleteModal
						open={openDeleteModal}
						setOpen={setOpenDeleteModal}
						listSaved={list}
					/>
				)}
			</div>
		);
	};

	if (!isMobile && user) {
		return <Navbar>{renderList()}</Navbar>;
	}

	return <div className="w-full p-8">{renderList()}</div>;
};

export default ListShared;
